import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";

interface RoomContentRow {
  room_type: string;
  title_de: string; title_en: string; title_fr: string; title_es: string; title_it: string;
  description_de: string; description_en: string; description_fr: string; description_es: string; description_it: string;
  highlight_de: string; highlight_en: string; highlight_fr: string; highlight_es: string; highlight_it: string;
  highlight_sub_de: string; highlight_sub_en: string; highlight_sub_fr: string; highlight_sub_es: string; highlight_sub_it: string;
  amenities_de: string; amenities_en: string; amenities_fr: string; amenities_es: string; amenities_it: string;
  price_info_de: string; price_info_en: string; price_info_fr: string; price_info_es: string; price_info_it: string;
}

const langSuffix = { de: "_de", en: "_en", fr: "_fr", es: "_es", it: "_it" } as const;

let cachedRooms: RoomContentRow[] | null = null;
let fetchPromise: Promise<RoomContentRow[]> | null = null;

const fetchAll = async (): Promise<RoomContentRow[]> => {
  if (cachedRooms) return cachedRooms;
  if (fetchPromise) return fetchPromise;
  fetchPromise = (async () => {
    const { data } = await supabase.from("room_content").select("*");
    cachedRooms = (data as RoomContentRow[]) || [];
    fetchPromise = null;
    return cachedRooms;
  })();
  return fetchPromise;
};

export const useRoomContent = (roomType?: string) => {
  const { language } = useLanguage();
  const [rooms, setRooms] = useState<RoomContentRow[]>(cachedRooms || []);

  useEffect(() => { fetchAll().then(setRooms); }, []);

  const getRoom = useCallback((type: string) => {
    const row = rooms.find((r) => r.room_type === type);
    if (!row) return null;
    const s = langSuffix[language];
    return {
      title: (row as any)[`title${s}`] || row.title_de,
      description: (row as any)[`description${s}`] || row.description_de,
      highlight: (row as any)[`highlight${s}`] || row.highlight_de,
      highlightSub: (row as any)[`highlight_sub${s}`] || row.highlight_sub_de,
      amenities: JSON.parse((row as any)[`amenities${s}`] || row.amenities_de || "[]") as string[],
      priceInfo: (row as any)[`price_info${s}`] || row.price_info_de,
    };
  }, [rooms, language]);

  const current = roomType ? getRoom(roomType) : null;

  return { getRoom, current, rooms, loading: rooms.length === 0 && !cachedRooms };
};

export const invalidateRoomCache = () => { cachedRooms = null; fetchPromise = null; };
